// Risk Category List Component - Groups risks by severity
// Works with existing RiskCategoryCard.jsx

import { useState } from 'react';
import { Text, Card } from '../ui';
import RiskCategoryCard from './RiskCategoryCard';
import { RISK_CATEGORIES, RISK_SYSTEMS, getRiskLevel } from '../../config/riskCategories';

/**
 * Convert riskScores object to risk items for RiskCategoryCard
 */
function mapScoreToRiskItem(categoryId, score) {
    const category = RISK_CATEGORIES[categoryId];
    if (!category) return null;

    const level = getRiskLevel(score);
    const system = RISK_SYSTEMS[category.system];

    return {
        id: categoryId,
        name_vi: category.name,
        name_cn: category.name_cn,
        icon: category.icon,
        score,
        level: {
            code: level === RISK_LEVELS?.VERY_LOW ? 'very_low' :
                level === RISK_LEVELS?.LOW ? 'low' :
                    level === RISK_LEVELS?.MODERATE ? 'moderate' :
                        level === RISK_LEVELS?.HIGH ? 'high' : 'very_high',
            label: level.label
        },
        needs_attention: score >= 60,
        tcm_syndromes: category.tcm_syndromes?.map(s => ({ name_vi: s, correlation: 0.8 })) || [],
        system_name: system?.name || ''
    };
}

// Import RISK_LEVELS
import { RISK_LEVELS } from '../../config/riskCategories';

/**
 * Risk Category List - Groups and displays risk cards
 */
export function RiskCategoryList({ riskScores = {}, maxVisible = 5, showTitle = true }) {
    const [expandedId, setExpandedId] = useState(null);
    const [showAll, setShowAll] = useState(false);

    // Convert scores to grouped items
    const grouped = { high: [], medium: [], low: [] };

    Object.entries(riskScores).forEach(([categoryId, score]) => {
        if (!RISK_CATEGORIES[categoryId]) return;

        const item = mapScoreToRiskItem(categoryId, score);
        if (!item) return;

        if (score >= 60) grouped.high.push(item);
        else if (score >= 40) grouped.medium.push(item);
        else grouped.low.push(item);
    });

    // Sort each group by score descending
    Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => b.score - a.score);
    });

    const handleToggle = (id) => (isExpanded) => {
        setExpandedId(isExpanded ? id : null);
    };

    const renderGroup = (items, title, icon, colorClass) => {
        if (items.length === 0) return null;

        const visibleItems = showAll ? items : items.slice(0, maxVisible);
        const hasMore = items.length > maxVisible && !showAll;

        return (
            <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{icon}</span>
                    <Text className={`font-semibold text-sm ${colorClass}`}>
                        {title} ({items.length})
                    </Text>
                </div>
                <div className="space-y-2">
                    {visibleItems.map((item) => (
                        <RiskCategoryCard
                            key={item.id}
                            risk={item}
                            expanded={expandedId === item.id}
                            onToggle={handleToggle(item.id)}
                        />
                    ))}
                </div>
                {hasMore && (
                    <button
                        onClick={() => setShowAll(true)}
                        className="w-full py-2 mt-2 text-sm text-theme-secondary hover:text-primary transition-all font-medium border border-dashed border-theme rounded-xl"
                    >
                        Xem thêm {items.length - maxVisible} mục...
                    </button>
                )}
            </div>
        );
    };

    const totalItems = grouped.high.length + grouped.medium.length + grouped.low.length;

    return (
        <Card className="mb-4">
            {showTitle && (
                <div className="flex items-center justify-between mb-4">
                    <Text className="font-semibold">📊 18 Hạng mục Rủi ro</Text>
                    <Text variant="small" className="text-theme-secondary">
                        {totalItems} mục
                    </Text>
                </div>
            )}

            {renderGroup(grouped.high, 'Cần chú ý', '⚠️', 'text-error')}
            {renderGroup(grouped.medium, 'Trung bình', '⚡', 'text-warning')}
            {renderGroup(grouped.low, 'Tốt', '✅', 'text-success')}

            {showAll && totalItems > maxVisible && (
                <button
                    onClick={() => setShowAll(false)}
                    className="w-full py-2 text-sm text-theme-secondary hover:underline"
                >
                    Thu gọn
                </button>
            )}

            {totalItems === 0 && (
                <div className="text-center py-8 text-theme-secondary">
                    <Text>Chưa có dữ liệu rủi ro</Text>
                </div>
            )}
        </Card>
    );
}

export default RiskCategoryList;
